import React from "react";
import { Box, Typography } from "@mui/material";
import { ChevronLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Badge } from "../../components/Badge.jsx";
import { useAuth } from "../../components/contexts/AuthContext";
import { DOCKED_HEADER_HEIGHT, DOCKED_SIDEBAR_WIDTH, DOCKED_SIDEBAR_WIDTH_ICON } from "../../components/Sidebar.jsx";
import { useTheme } from "../../theme/useTheme";
import { getGroupedSettingsSections } from "./settingsSections";
import { readSettingsReturnPath } from "./settingsPaths";

/** Menú de Ajustes en escritorio: va junto al sidebar principal, que en /ajustes queda en iconos. */
export function SettingsSidebar() {
  const { theme, colors, neutrals } = useTheme();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const groups = getGroupedSettingsSections(user);

  return (
    <Box
      component="nav"
      aria-label="Ajustes"
      sx={{
        position: "fixed",
        top: DOCKED_HEADER_HEIGHT,
        bottom: 0,
        left: DOCKED_SIDEBAR_WIDTH_ICON,
        zIndex: 1000,
        width: DOCKED_SIDEBAR_WIDTH,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        padding: "8px 12px 12px 8px",
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${neutrals.gray[200]}`,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => navigate(readSettingsReturnPath())}
        aria-label="Salir de Ajustes"
        sx={{
          all: "unset",
          boxSizing: "border-box",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 36,
          px: 1,
          mb: 0.5,
          borderRadius: "10px",
          color: theme.palette.text.primary,
          "&:hover": { backgroundColor: neutrals.gray[100] },
          "&:focus-visible": { outline: `2px solid ${colors.brand.pink}`, outlineOffset: -2 },
        }}
      >
        <ChevronLeft size={18} />
        <Typography sx={{ fontSize: "15px", fontWeight: 600, lineHeight: 1 }}>Ajustes</Typography>
      </Box>

      {groups.map((group) => (
        <React.Fragment key={group.id}>
          {/* Mismo estilo que las secciones y entradas del menú principal (AppLayout) */}
          <Typography
            sx={{
              padding: "24px 12px 6px 12px",
              fontSize: "14px",
              fontWeight: 400,
              color: colors.nav.section,
              lineHeight: 1.2,
            }}
          >
            {group.title}
          </Typography>
          {group.items.map((section) => {
            const isActive = pathname === section.path;
            const Icon = section.icon;
            return (
              <Box
                key={section.id}
                component={Link}
                to={section.path}
                aria-current={isActive ? "page" : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  height: 34,
                  flexShrink: 0,
                  px: "16px",
                  mb: "2px",
                  borderRadius: "10px",
                  textDecoration: "none",
                  backgroundColor: isActive ? colors.secondary.main : "transparent",
                  "&:hover": { backgroundColor: isActive ? colors.secondary.main : neutrals.gray[50] },
                }}
              >
                <Icon size={16} color={isActive ? colors.brand.pink : colors.nav.icon} />
                <Typography
                  sx={{
                    flex: 1,
                    fontWeight: isActive ? 600 : 400,
                    fontSize: "14px",
                    color: isActive ? colors.brand.pink : colors.nav.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {section.label}
                </Typography>
                {section.badge && <Badge variant="destructive">{section.badge}</Badge>}
              </Box>
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
}
